import { Link } from "@tanstack/react-router";
import type { LeaderboardPlayer } from "@/api/ranking";
import { getPointsBadge } from "@/lib/utils";
import { Loader } from "@/components/general/common/loader";

const PeriodBadge = ({ label, value }: { label: string; value: number | null }) => {
    const badge = value != null ? getPointsBadge(value) : null;
    return (
        <div className="flex flex-col items-center gap-0.5 w-9">
            <span className="text-[9px] uppercase font-bold text-on-surface-variant tracking-wider">{label}</span>
            {value != null && badge ? (
                <span
                    className={`w-full text-center text-xs font-bold tabular-nums py-0.5 rounded border leading-none ${badge.bg} ${badge.text} ${badge.border}`}
                >
                    {value}
                </span>
            ) : (
                <span className="text-xs text-on-surface-variant leading-none">–</span>
            )}
        </div>
    );
};

interface LeaderboardMobileCardsProps {
    data: LeaderboardPlayer[];
    isLoading?: boolean;
}

export const LeaderboardMobileCards = ({ data, isLoading = false }: LeaderboardMobileCardsProps) => {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[200px] bg-surface-container-lowest rounded-xl">
                <Loader message="Loading analytics data..." />
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-[200px] bg-surface-container-lowest rounded-xl">
                <span className="text-on-surface-variant text-sm">No analytics data available</span>
            </div>
        );
    }

    const sorted = [...data].sort((a, b) => (b.totalPoints ?? 0) - (a.totalPoints ?? 0));

    return (
        <div className="flex flex-col bg-surface-container-lowest rounded-xl overflow-hidden">
            {sorted.map((player, index) => {
                const rank = index + 1;
                const role = player.role.length > 0 ? player.role[0] : null;
                const total = player.totalPoints;
                const totalBadge = total ? getPointsBadge(total) : null;
                const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";

                return (
                    <div key={player.playerId} className={`flex items-center gap-3 pl-0 pr-3 py-3 ${rowBg}`}>
                        <span
                            className={`font-headline font-bold text-sm w-9 text-center shrink-0 tabular-nums ${
                                rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                            }`}
                        >
                            #{rank.toString().padStart(2, "0")}
                        </span>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <Link
                                    to="/player/$playerId"
                                    params={{ playerId: player.playerId.toString() }}
                                    className="font-bold text-sm text-on-surface hover:text-primary truncate"
                                >
                                    {player.playerName} {player.playerLastName}
                                </Link>
                                {role && (
                                    <span className="px-1.5 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant shrink-0">
                                        {role}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-end gap-2">
                                <PeriodBadge label="P1" value={player.period1} />
                                <PeriodBadge label="P2" value={player.period2} />
                                <PeriodBadge label="P3" value={player.period3} />
                            </div>
                        </div>

                        <div className="shrink-0 w-12 text-center">
                            {totalBadge && total ? (
                                <div
                                    className={`w-full text-center tabular-nums py-1 rounded font-bold text-sm border ${totalBadge.bg} ${totalBadge.text} ${totalBadge.border}`}
                                >
                                    {total}
                                </div>
                            ) : (
                                <span className="text-on-surface-variant text-sm">–</span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
